import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Edit3,
  Trash2,
  Shield,
  Eye,
  EyeOff,
  Mail,
  Calendar,
  Search,
  Filter,
  AlertTriangle,
  Loader2,
  RefreshCcw,
  Clock
} from 'lucide-react';
import { api } from '../../services/api';

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'admin', label: 'Administrator', color: 'bg-red-100 text-red-800' },
  { value: 'manager', label: 'Manager', color: 'bg-blue-100 text-blue-800' },
  { value: 'dispatcher', label: 'Dispatcher', color: 'bg-green-100 text-green-800' },
  { value: 'driver', label: 'Driver', color: 'bg-gray-100 text-gray-800' },
  { value: 'mechanic', label: 'Mechanic', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'viewer', label: 'Viewer', color: 'bg-purple-100 text-purple-800' }
];

const USERS_PAGE_SIZE = 25;

const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending'
};

const USER_STATUS_CONFIG = {
  [USER_STATUS.ACTIVE]: {
    label: 'Active',
    className: 'bg-green-100 text-green-800',
    icon: Eye
  },
  [USER_STATUS.INACTIVE]: {
    label: 'Inactive',
    className: 'bg-red-100 text-red-800',
    icon: EyeOff
  },
  [USER_STATUS.PENDING]: {
    label: 'Pending Invite',
    className: 'bg-yellow-100 text-yellow-800',
    icon: Clock
  }
};

const isBrowser = typeof window !== 'undefined';

const safeJsonParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('Failed to parse JSON from localStorage');
    return null;
  }
};

const resolveCompanyContext = () => {
  if (!isBrowser) {
    return { companyId: null, currentUserId: null };
  }

  const context = {
    companyId: null,
    currentUserId: null
  };

  const storedCompanyId = window.localStorage.getItem('companyId');
  if (storedCompanyId && storedCompanyId !== 'demo-company-id') {
    context.companyId = storedCompanyId;
  }

  const storedCompanyData = safeJsonParse(window.localStorage.getItem('company'));
  if (!context.companyId && storedCompanyData?.id && storedCompanyData.id !== 'demo-company-id') {
    context.companyId = storedCompanyData.id;
  }

  const storedUserData = safeJsonParse(window.localStorage.getItem('user'));
  if (storedUserData) {
    context.currentUserId = storedUserData.uid || storedUserData.id || null;
    if (!context.companyId && storedUserData.companyId && storedUserData.companyId !== 'demo-company-id') {
      context.companyId = storedUserData.companyId;
    }
  }

  return context;
};

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value.toDate === 'function') {
    const converted = value.toDate();
    return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
  }

  const seconds = value.seconds ?? value._seconds;
  if (typeof seconds === 'number') {
    return new Date(seconds * 1000);
  }

  return null;
};

const formatDate = (value, fallback = '—') => {
  const date = toJsDate(value);
  if (!date) return fallback;

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const countNewUsersWithinDays = (users, days = 30) => {
  if (!Array.isArray(users) || !users.length) {
    return 0;
  }

  const threshold = Date.now() - days * 24 * 60 * 60 * 1000;
  return users.filter((user) => {
    const createdAt = toJsDate(user.createdAt);
    return createdAt && createdAt.getTime() >= threshold;
  }).length;
};

const getFullNameFromUser = (user) => {
  if (!user) return '';
  const parts = [];
  if (user.firstName) parts.push(user.firstName.trim());
  if (user.lastName) parts.push(user.lastName.trim());
  return parts.join(' ').trim();
};

const splitFullName = (fullName) => {
  const trimmed = (fullName || '').trim();
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const parts = trimmed.split(/\s+/);
  const firstName = parts.shift();
  const lastName = parts.length ? parts.join(' ') : '';
  return { firstName, lastName };
};

const resolveUserStatus = (user) => {
  if (!user) return USER_STATUS.PENDING;
  if (user.isActive === false) return USER_STATUS.INACTIVE;
  if (!user.lastLogin) return USER_STATUS.PENDING;
  return USER_STATUS.ACTIVE;
};

const sanitizePayload = (payload) => {
  const cleaned = { ...payload };
  Object.keys(cleaned).forEach((key) => {
    if (cleaned[key] === '' || cleaned[key] === undefined || cleaned[key] === null) {
      delete cleaned[key];
    }
  });
  return cleaned;
};

const UserControl = ({ onChange = () => {} }) => {
  const { companyId, currentUserId } = useMemo(resolveCompanyContext, []);
  const [view, setView] = useState('list');
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 1,
    hasMore: false,
    hasPrevious: false
  });
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [pendingToggleIds, setPendingToggleIds] = useState(new Set());

  const createInitialFormState = useCallback(() => ({
    fullName: selectedUser ? getFullNameFromUser(selectedUser) : '',
    email: selectedUser?.email || '',
    role: selectedUser?.role || 'manager'
  }), [selectedUser]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchUsers = useCallback(async () => {
    if (!companyId) {
      setUsersError('No company context found. Please sign out and sign back in.');
      setUsers([]);
      setUsersLoading(false);
      return;
    }

    setUsersLoading(true);
    setUsersError(null);

    try {
      const params = {
        page,
        limit: USERS_PAGE_SIZE
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';

      const response = await api.users.list(params);
      const total = response.pagination?.total ?? response.users?.length ?? 0;
      const totalPages = response.pagination?.totalPages ?? Math.max(1, Math.ceil(total / USERS_PAGE_SIZE));

      // Applicants (driver application in progress) are managed from Drivers › Applications, not here.
      setUsers((response.users || []).filter((user) => user.role !== 'applicant'));
      setPaginationMeta({
        total,
        totalPages,
        hasMore: response.pagination?.hasMore ?? page < totalPages,
        hasPrevious: response.pagination?.hasPrevious ?? page > 1
      });
    } catch (error) {
      setUsersError(error.getUserFriendlyMessage ? error.getUserFriendlyMessage() : 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [companyId, debouncedSearch, roleFilter, statusFilter, page]);

  const fetchStats = useCallback(async () => {
    if (!companyId) {
      setStats(null);
      return;
    }

    setStatsLoading(true);
    setStatsError(null);

    try {
      const response = await api.users.stats();
      setStats(response.stats);
    } catch (error) {
      // Managers only; surface friendly message but don't block UI
      setStatsError(error.getUserFriendlyMessage ? error.getUserFriendlyMessage() : 'Unable to load user statistics');
    } finally {
      setStatsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refreshData = useCallback(() => {
    fetchUsers();
    fetchStats();
    onChange();
  }, [fetchUsers, fetchStats, onChange]);

  const handleAddUser = () => {
    setSelectedUser(null);
    setView('add');
    setActionError(null);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setView('edit');
    setActionError(null);
  };

  const handleDeletePrompt = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
    setActionError(null);
  };

  const updatePendingToggle = (userId, shouldAdd) => {
    setPendingToggleIds((prev) => {
      const next = new Set(prev);
      if (shouldAdd) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  };

  const handleToggleStatus = async (user) => {
    const userId = user.id || user.uid;
    if (!userId) return;

    updatePendingToggle(userId, true);
    setActionError(null);

    try {
      const nextStatus = !user.isActive;
      await api.users.update(userId, { isActive: nextStatus });
      setUsers((prev) =>
        prev.map((item) => {
          const itemId = item.id || item.uid;
          return itemId === userId ? { ...item, isActive: nextStatus } : item;
        })
      );
      fetchStats();
      onChange();
    } catch (error) {
      setActionError(error.getUserFriendlyMessage ? error.getUserFriendlyMessage() : 'Failed to update user status');
    } finally {
      updatePendingToggle(userId, false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    const userId = userToDelete.id || userToDelete.uid;
    if (!userId) return;
    setDeleteInProgress(true);
    setActionError(null);

    try {
      await api.users.delete(userId);
      setShowDeleteModal(false);
      setUserToDelete(null);
      refreshData();
    } catch (error) {
      setActionError(error.getUserFriendlyMessage ? error.getUserFriendlyMessage() : 'Failed to delete user');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const getRoleConfig = (role) => {
    return ROLE_OPTIONS.find((option) => option.value === role) || ROLE_OPTIONS.find((option) => option.value === 'viewer');
  };

  const totalUsers = stats?.total ?? users.length;
  const activeUsers = stats?.active ?? users.filter((user) => user.isActive).length;
  const adminCount = stats?.byRole?.admin ?? users.filter((user) => user.role === 'admin').length;
  const newUsers = countNewUsersWithinDays(users);

  const UserForm = () => {
    const [formData, setFormData] = useState(createInitialFormState);
    const [formError, setFormError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      setFormData(createInitialFormState());
    }, [createInitialFormState]);

    const handleSubmit = async (event) => {
      event.preventDefault();
      setFormError(null);

      if (!selectedUser && !companyId) {
        setFormError('Cannot create a user without a valid company.');
        return;
      }

      if (!formData.fullName.trim()) {
        setFormError('Full name is required.');
        return;
      }

      setSubmitting(true);

      const { firstName, lastName } = splitFullName(formData.fullName);

      if (!firstName) {
        setFormError('Full name must include at least a first name.');
        setSubmitting(false);
        return;
      }

      const payload = sanitizePayload({
        firstName,
        lastName,
        email: formData.email.trim().toLowerCase(),
        role: formData.role
      });

      if (!selectedUser) {
        payload.companyId = companyId;
      }

      try {
        if (selectedUser) {
          const targetUserId = selectedUser.id || selectedUser.uid;
          await api.users.update(targetUserId, payload);
        } else {
          await api.users.create(payload);
        }

        setView('list');
        setSelectedUser(null);
        refreshData();
      } catch (error) {
        setFormError(error.getUserFriendlyMessage ? error.getUserFriendlyMessage() : 'Failed to save user');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {formError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {formError}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            <input
              type="text"
              required
              value={formData.fullName}
              onChange={(e) => setFormData((prev) => ({ ...prev, fullName: e.target.value }))}
              placeholder="Jane Smith"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="inline h-4 w-4 mr-1" />
              Work Email
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="jane.smith@company.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {ROLE_OPTIONS.filter((role) => role.value !== 'super_admin').map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Permissions are assigned automatically based on role.</p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => {
              setView('list');
              setSelectedUser(null);
            }}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={`btn-primary ${submitting ? 'opacity-70 cursor-wait' : ''}`}
          >
            {submitting ? 'Saving...' : selectedUser ? 'Update User' : 'Invite User'}
          </button>
        </div>
      </form>
    );
  };

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              setView('list');
              setSelectedUser(null);
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            ← Back to Users
          </button>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {view === 'edit' ? 'Edit User' : 'Invite New User'}
          </h3>
          <p className="text-gray-600">
            {view === 'edit'
              ? 'Update the user\'s name, email, or role.'
              : 'Invite a teammate with role-based access (permissions are assigned automatically).'}
          </p>
        </div>
        <UserForm />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Users className="h-5 w-5 mr-2 text-blue-600" />
            User Management
          </h3>
          <p className="text-gray-600 mt-1">Invite, manage, and deactivate company users and access.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refreshData}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleAddUser}
            disabled={!companyId}
            className={`btn-primary inline-flex items-center ${!companyId ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invite User
          </button>
        </div>
      </div>

      {actionError && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md text-sm flex items-start">
          <AlertTriangle className="h-4 w-4 mr-2 mt-0.5" />
          <div>{actionError}</div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Roles</option>
              {ROLE_OPTIONS.filter((role) => role.value !== 'super_admin').map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {usersLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
            <span className="ml-3 text-gray-600">Loading users...</span>
          </div>
        ) : usersError ? (
          <div className="p-6 text-center">
            <p className="text-red-600 mb-3">{usersError}</p>
            <button
              onClick={fetchUsers}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-600">No users found for the selected filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => {
                  const roleConfig = getRoleConfig(user.role);
                  const userId = user.id || user.uid;
                  const isCurrentUser = userId === currentUserId;
                  const isToggling = userId ? pendingToggleIds.has(userId) : false;
                  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || 'Unnamed User';
                  const rowKey = userId || user.email;
                  const statusKey = resolveUserStatus(user);
                  const statusConfig = USER_STATUS_CONFIG[statusKey];
                  const StatusIcon = statusConfig?.icon;

                  return (
                    <tr key={rowKey} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-sm font-semibold">
                              {`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U'}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {displayName}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${roleConfig?.color || 'bg-gray-100 text-gray-800'}`}>
                          {roleConfig?.label || 'Viewer'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.department || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {statusKey === USER_STATUS.PENDING ? (
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.className}`}>
                            {StatusIcon ? <StatusIcon className="h-3 w-3 mr-1" /> : null}
                            {statusConfig.label}
                          </span>
                        ) : (
                          <button
                            onClick={() => handleToggleStatus(user)}
                            disabled={isCurrentUser || isToggling || !userId}
                            className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                              statusConfig.className
                            } ${isCurrentUser || isToggling || !userId ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            {StatusIcon ? <StatusIcon className="h-3 w-3 mr-1" /> : null}
                            {statusConfig.label}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePrompt(user)}
                            className="text-red-600 hover:text-red-900"
                            disabled={isCurrentUser || !userId}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Showing page {page} of {paginationMeta.totalPages} ({paginationMeta.total} users)
        </span>
        <div className="space-x-2">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={!paginationMeta.hasPrevious && page === 1}
            className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setPage((prev) => prev + 1)}
            disabled={!paginationMeta.hasMore}
            className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">{totalUsers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Eye className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Active Users</p>
              <p className="text-2xl font-semibold text-gray-900">{activeUsers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Admins</p>
              <p className="text-2xl font-semibold text-gray-900">{adminCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">New This Month</p>
              <p className="text-2xl font-semibold text-gray-900">{newUsers}</p>
            </div>
          </div>
        </div>
      </div>

      {statsLoading && !statsError && (
        <p className="text-xs text-gray-500">Refreshing statistics...</p>
      )}

      {statsError && (
        <p className="text-xs text-gray-500">{statsError}</p>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="delete-user-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={cancelDeleteUser}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="delete-user-title">
                    Delete User
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to remove <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong>? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200 ${deleteInProgress ? 'opacity-70 cursor-wait' : ''}`}
                  onClick={confirmDeleteUser}
                  disabled={deleteInProgress}
                >
                  {deleteInProgress ? 'Deleting...' : 'Delete User'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm transition-colors duration-200"
                  onClick={cancelDeleteUser}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserControl;
